<script setup lang="ts">
import { Form, Link, router, usePage } from "@inertiajs/vue3";
import {
  ArrowLeftIcon,
  AtSignIcon,
  FileIcon,
  ImageIcon,
  PaperclipIcon,
  SendIcon,
  XIcon,
} from "lucide-vue-next";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import GroupHoverCard from "@/components/group-hover-card.vue";
import MentionText from "@/components/mention-text.vue";
import OfflineStateBanner from "@/components/offline-state-banner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button/index";
import { Progress } from "@/components/ui/progress/index";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNetworkStatus } from "@/composables/use-network-status";
import { markRead } from "@/composables/use-thread-read-status";
import DiscussionThreadTags from "@/features/discussion-threads/components/discussion-thread-tags.vue";
import { formatByteSize } from "@/features/discussion-threads/formatters";
import { loadThreadMessages, saveThreadMessages } from "@/lib/discussion-db";
import {
  mentionHandleFor,
  mentionedEntitiesFromText,
} from "@/lib/discussion-mentions";
import { getUserAvatarColor } from "@/lib/discussion-thread-helpers";
import type { OfflineQueuedMessage } from "@/lib/offline-queue";
import {
  enqueueOfflineMessage,
  getOfflineMessagesForThread,
  removeOfflineMessage,
} from "@/lib/offline-queue";
import { useCableSubscription } from "@/lib/use-cable";
import { organizationDiscussionThreadDiscussionMessagesPath } from "@/routes";
import type {
  DiscussionMessage,
  DiscussionThread,
  MentionableEntity,
  MentionableGroup,
  MentionableUser,
} from "@/types";
import { initialsFromName } from "@/utils/initials";

const uniqueBy = <T>(items: T[], getKey: (item: T) => string) => {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(item);
  }
  return result;
};

const sortByCreatedAt = (items: DiscussionMessage[]) =>
  [...items].sort((left, right) => {
    const leftTime = new Date(left.created_at).getTime();
    const rightTime = new Date(right.created_at).getTime();
    if (leftTime !== rightTime) return leftTime - rightTime;
    return (left.id ?? 0) - (right.id ?? 0);
  });

const scopedMessagesForThread = (
  messages: DiscussionMessage[],
  threadId: number,
) => messages.filter((message) => message.thread_id === threadId);

const MESSAGE_PARTIAL_PROPS = ["messages", "attachments", "messages_thread_id"] as const;

defineOptions({ inheritAttrs: false });

interface Props {
  thread: DiscussionThread;
  messages: DiscussionMessage[];
  mentionables: MentionableEntity[];
  variant?: "desktop" | "mobile";
  backHref?: string;
  embedded?: boolean;
  showHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "desktop",
  backHref: "",
  embedded: false,
  showHeader: true,
});

const page = usePage<{
  auth?: {
    organization?: { id: number; name?: string };
    user?: {
      id: number;
      name: string;
      email: string;
    };
  };
}>();

const organizationId = computed(() => page.props.auth?.organization?.id);
const currentUser = computed(() => page.props.auth?.user);
const { isOnline } = useNetworkStatus();

const liveMessages = ref<DiscussionMessage[]>([...props.messages]);
const threadSnapshot = ref<DiscussionThread>({ ...props.thread });
const mentionables = ref<MentionableEntity[]>([...props.mentionables]);
const clearThreadScopedState = () => {
  liveMessages.value = [];
  mentionables.value = [];
};
const queuedMessages = ref<OfflineQueuedMessage[]>([]);
const flushing = ref(false);
const composerError = ref<string | null>(null);
const hasCachedThread = ref<boolean | null>(null);
const offlineLoadToken = ref(0);

const messageBody = ref("");
const attachments = ref<File[]>([]);
const cursorPosition = ref(0);
const mentionStartIndex = ref<number | null>(null);
const mentionQuery = ref("");
const showMentions = ref(false);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const messagesEndRef = ref<HTMLDivElement | null>(null);
const DEBUG_OFFLINE = false;
interface ComposerSnapshot {
  body: string;
  attachments: File[];
}

const composerSnapshot = ref<ComposerSnapshot | null>(null);

const hasBackLink = computed(() => Boolean(props.backHref) && Boolean(organizationId.value));
const containerClass = computed(() => {
  if (props.embedded) return "";
  return props.variant === "desktop"
    ? "bg-[linear-gradient(180deg,hsl(var(--background))_0%,hsl(var(--muted)/0.24)_100%)]"
    : "min-h-[calc(100dvh-11.5rem)] rounded-2xl border border-border/60 bg-background shadow-sm";
});
const markCurrentThreadAsRead = (updatedAt?: string) => {
  if (typeof window === "undefined") return;
  if (!updatedAt) return;
  markRead(props.thread.id, updatedAt);
};

onMounted(() => {
  markCurrentThreadAsRead(props.thread.updated_at);
});

const loadQueuedMessages = async () => {
  queuedMessages.value = await getOfflineMessagesForThread(props.thread.id);
};

const pruneQueuedMessages = async (messages: DiscussionMessage[]) => {
  if (queuedMessages.value.length === 0) return;
  const clientIds = new Set(
    messages.map((message) => message.client_message_id).filter(Boolean),
  );
  if (clientIds.size === 0) return;

  const idsToRemove = queuedMessages.value
    .filter((item) => clientIds.has(item.id))
    .map((item) => item.id);
  if (idsToRemove.length === 0) return;

  await Promise.all(idsToRemove.map((id) => removeOfflineMessage(id)));
  await loadQueuedMessages();
};

type QueuePostResult =
  | { status: "success" }
  | { status: "validation"; errors: Record<string, unknown> }
  | { status: "network" }
  | { status: "cancel" };

const postQueuedMessageViaInertia = (item: OfflineQueuedMessage) =>
  new Promise<QueuePostResult>((resolve) => {
    const orgId = organizationId.value;
    if (!orgId) {
      resolve({ status: "network" });
      return;
    }

    let settled = false;
    const finish = (result: QueuePostResult) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timeoutId = setTimeout(() => {
      finish({ status: "network" });
    }, 15000);

    router.post(
      organizationDiscussionThreadDiscussionMessagesPath(orgId, props.thread.id),
      {
        discussion_message: {
          body: item.body,
          client_message_id: item.id,
          mentioned_user_ids: item.mentionUserIds,
          mentioned_group_ids: item.mentionGroupIds,
        },
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: [...MESSAGE_PARTIAL_PROPS],

        onSuccess: () => {
          clearTimeout(timeoutId);
          finish({ status: "success" });
        },
        onError: (errors) => {
          clearTimeout(timeoutId);
          finish({ status: "validation", errors: errors as Record<string, unknown> });
        },
        onCancel: () => {
          clearTimeout(timeoutId);
          finish({ status: "cancel" });
        },
        onFinish: () => {
          clearTimeout(timeoutId);
          if (!settled) {
            finish({ status: "network" });
          }
        },
      },
    );
  });

const flushQueuedMessages = async () => {
  if (!organizationId.value || flushing.value) return;
  if (!isOnline.value) return;

  const queued = await getOfflineMessagesForThread(props.thread.id);
  if (queued.length === 0) return;

  flushing.value = true;
  composerError.value = null;

  let shouldReloadMessages = false;
  let droppedByValidation = 0;

  try {
    for (const item of queued) {
      const result = await postQueuedMessageViaInertia(item);

      if (result.status === "success") {
        shouldReloadMessages = true;
        continue;
      }

      if (result.status === "validation") {
        await removeOfflineMessage(item.id);
        droppedByValidation += 1;
        shouldReloadMessages = true;
        if (DEBUG_OFFLINE) {
          console.warn("[OfflineQueue] Validation error, dropping message", item.id, result.errors);
        }
        continue;
      }

      if (DEBUG_OFFLINE) {
        console.warn("[OfflineQueue] Retry deferred", item.id, result.status);
      }
      break;
    }
  } finally {
    await loadQueuedMessages();
    if (droppedByValidation > 0) {
      composerError.value =
        droppedByValidation === 1
          ? "Se descartó un mensaje en cola por validación."
          : `Se descartaron ${droppedByValidation} mensajes en cola por validación.`;
    }
    if (shouldReloadMessages) {
      router.reload({ only: [...MESSAGE_PARTIAL_PROPS], preserveScroll: true });
    }
    flushing.value = false;
  }
};


watch(
  () => props.messages,
  (incomingMessages) => {
    const scopedMessages = scopedMessagesForThread(incomingMessages, props.thread.id);
    if (scopedMessages.length !== incomingMessages.length) {
      liveMessages.value = [];
      return;
    }

    liveMessages.value = sortByCreatedAt(scopedMessages);
    void pruneQueuedMessages(scopedMessages);
  },
  { immediate: true },
);

watch(
  () => props.thread,
  (thread) => {
    threadSnapshot.value = { ...thread };
  },
  { deep: true },
);

watch(
  () => props.mentionables,
  (users) => {
    mentionables.value = [...users];
  },
  { deep: true },
);

watch(
  () => props.thread.id,
  (threadId, previousThreadId) => {
    const threadChanged =
      previousThreadId !== undefined && threadId !== previousThreadId;

    if (threadChanged) {
      offlineLoadToken.value += 1;
      clearThreadScopedState();
    }

    threadSnapshot.value = { ...props.thread };
    markCurrentThreadAsRead(props.thread.updated_at);
    hasCachedThread.value = null;
    void loadQueuedMessages();
  },
  { immediate: true },
);

watch(
  () => props.thread.updated_at,
  (updatedAt) => {
    markCurrentThreadAsRead(updatedAt);
  },
);

watch(
  () => [liveMessages.value.length, queuedMessages.value.length],
  async () => {
    await nextTick();
    messagesEndRef.value?.scrollIntoView({ behavior: "smooth", block: "end" });
  },
  { immediate: true },
);

watch(
  [organizationId, () => props.thread.id, threadSnapshot, liveMessages, mentionables],
  ([orgId, activeThreadId]) => {
    if (!orgId) return;

    if (threadSnapshot.value.id !== activeThreadId) return;
    const scopedLiveMessages = scopedMessagesForThread(
      liveMessages.value,
      activeThreadId,
    );
    if (scopedLiveMessages.length !== liveMessages.value.length) return;

    void saveThreadMessages(
      orgId,
      activeThreadId,
      threadSnapshot.value,
      scopedLiveMessages,
      mentionables.value,
    );
  },
  { deep: true },
);

watch(
  [isOnline, organizationId, () => props.thread.id],
  ([online, orgId, threadId]) => {
    const requestToken = offlineLoadToken.value;

    if (online || !orgId) {
      hasCachedThread.value = null;
      return;
    }

    void (async () => {
      const cached = await loadThreadMessages(orgId, threadId);
      if (requestToken !== offlineLoadToken.value) return;

      if (cached?.thread.id !== threadId) {
        hasCachedThread.value = false;
        clearThreadScopedState();
        return;
      }

      const scopedCachedMessages = scopedMessagesForThread(cached.messages, threadId);
      if (scopedCachedMessages.length !== cached.messages.length) {
        hasCachedThread.value = false;
        clearThreadScopedState();
        return;
      }

      threadSnapshot.value = cached.thread;
      liveMessages.value = sortByCreatedAt(scopedCachedMessages);
      if (cached.mentionables?.length > 0) {
        mentionables.value = cached.mentionables;
      }
      hasCachedThread.value = true;
    })();
  },
  { immediate: true },
);

const onlineStateInitialized = ref(false);

watch(
  isOnline,
  (online, wasOnline) => {
    if (!onlineStateInitialized.value) {
      onlineStateInitialized.value = true;
      if (online) {
        void flushQueuedMessages();
      }
      return;
    }

    if (online && !wasOnline) {
      void flushQueuedMessages();
      router.reload({ only: [...MESSAGE_PARTIAL_PROPS], preserveScroll: true });
    }
  },
  { immediate: true },
);

useCableSubscription<{ type: string; thread_id?: number; thread_updated_at?: string; message?: DiscussionMessage; client_message_id?: string }>(
  { channel: "DiscussionMessagesChannel" },
  (msg) => {
    if (msg.type !== "discussion.message.created") return;
    if (!msg.message || msg.thread_id !== props.thread.id) return;

    const alreadyExists = liveMessages.value.some(
      (message) => message.id === msg.message?.id,
    );
    if (alreadyExists) return;

    liveMessages.value = sortByCreatedAt([...liveMessages.value, msg.message]);
    const threadUpdatedAt = msg.thread_updated_at || msg.message.created_at;
    threadSnapshot.value = { ...threadSnapshot.value, updated_at: threadUpdatedAt };
    markRead(props.thread.id, threadUpdatedAt);

    if (msg.client_message_id) {
      void (async () => {
        await removeOfflineMessage(msg.client_message_id!);
        await loadQueuedMessages();
      })();
    }
  },
);

useCableSubscription<{ type: string; thread_id?: number }>(
  { channel: "DiscussionMessagesChannel" },
  (msg) => {
    if (msg.thread_id !== props.thread.id) return;

    if (msg.type === "discussion.thread.archived") {
      threadSnapshot.value = { ...threadSnapshot.value, archived: true };
    } else if (msg.type === "discussion.thread.unarchived") {
      threadSnapshot.value = { ...threadSnapshot.value, archived: false };
    }
  },
);

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  });

const formatMessageTime = (value: string) => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "";

  const elapsedMs = Math.max(Date.now() - timestamp, 0);
  const elapsedMinutes = Math.floor(elapsedMs / 60_000);
  if (elapsedMinutes < 1) return "hace 1min";
  if (elapsedMinutes < 60) return `hace ${elapsedMinutes}min`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `hace ${elapsedHours}h`;

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `hace ${elapsedDays}d`;

  return new Date(timestamp).toLocaleDateString("es-AR", { day: "numeric", month: "short" });
};

const initialsFor = (name: string) => initialsFromName(name);

const MESSAGE_GROUP_THRESHOLD_MS = 5 * 60_000; // 5 minutes

const isFirstInGroup = (index: number): boolean => {
	if (index === 0) return true;
	const current = displayMessages.value[index];
	const previous = displayMessages.value[index - 1];
	if (!current || !previous) return true;
	if (current.user.id !== previous.user.id) return true;
	const gap = new Date(current.created_at).getTime() - new Date(previous.created_at).getTime();
	return gap > MESSAGE_GROUP_THRESHOLD_MS;
};

const isNewDay = (index: number): boolean => {
	if (index === 0) return true;
	const current = displayMessages.value[index];
	const previous = displayMessages.value[index - 1];
	if (!current || !previous) return true;
	const currentDate = new Date(current.created_at).toDateString();
	const previousDate = new Date(previous.created_at).toDateString();
	return currentDate !== previousDate;
};

const formatDaySeparator = (dateStr: string): string => {
	const date = new Date(dateStr);
	const now = new Date();
	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);

	if (date.toDateString() === now.toDateString()) return 'Hoy';
	if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
	return date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
};
const composerLocked = computed(() => threadSnapshot.value.archived);
const canSendMessage = computed(() => !composerLocked.value && messageBody.value.trim().length > 0);

const clearMentionState = () => {
  showMentions.value = false;
  mentionStartIndex.value = null;
  mentionQuery.value = "";
};

const autoResizeTextarea = () => {
  if (!textareaRef.value) return;
  textareaRef.value.style.height = "auto";
  textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 128)}px`;
};

const syncInputFiles = () => {
  if (!fileInputRef.value || typeof DataTransfer === "undefined") return;

  const dataTransfer = new DataTransfer();
  attachments.value.forEach((file) => dataTransfer.items.add(file));
  fileInputRef.value.files = dataTransfer.files;
};

const openFilePicker = () => {
  if (!fileInputRef.value) return;
  fileInputRef.value.value = "";
  fileInputRef.value.click();
};

const updateMentionState = (value: string, caretPosition: number) => {
  const textBeforeCursor = value.slice(0, caretPosition);
  const atIndex = textBeforeCursor.lastIndexOf("@");

  if (atIndex < 0) {
    clearMentionState();
    return;
  }

  const charBeforeAt = textBeforeCursor.slice(atIndex - 1, atIndex);
  if (charBeforeAt && !/[\s([{]/.test(charBeforeAt)) {
    clearMentionState();
    return;
  }

  const query = textBeforeCursor.slice(atIndex + 1);
  if (/[\s\n]/.test(query)) {
    clearMentionState();
    return;
  }

  mentionStartIndex.value = atIndex;
  mentionQuery.value = query.toLowerCase();
  showMentions.value = true;
};

const handleBodyInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  messageBody.value = target.value;
  cursorPosition.value = target.selectionStart ?? target.value.length;
  updateMentionState(messageBody.value, cursorPosition.value);
  autoResizeTextarea();
};

const handleCursorUpdate = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  cursorPosition.value = target.selectionStart ?? messageBody.value.length;
  updateMentionState(messageBody.value, cursorPosition.value);
};

const filteredMentionables = computed(() => {
  if (!showMentions.value) return [];
  const query = mentionQuery.value.trim();
  return mentionables.value
    .filter((entity) => {
      if (query.length === 0) return true;
      const fields = [entity.name.toLowerCase()];
      if (entity.type === "user") {
        fields.push(entity.email.toLowerCase());
      }
      return fields.some((field) => field.includes(query));
    })
    .slice(0, 6);
});
const handleMentionSelect = async (entity: MentionableEntity) => {
  if (mentionStartIndex.value === null) return;
  const beforeMention = messageBody.value.slice(0, mentionStartIndex.value);
  const afterCursor = messageBody.value.slice(cursorPosition.value);
  const mentionText = `@${mentionHandleFor(entity)} `;
  messageBody.value = `${beforeMention}${mentionText}${afterCursor}`;
  clearMentionState();
  await nextTick();
  autoResizeTextarea();
  if (!textareaRef.value) return;
  const nextCursorPosition = beforeMention.length + mentionText.length;
  textareaRef.value.focus();
  textareaRef.value.setSelectionRange(nextCursorPosition, nextCursorPosition);
  cursorPosition.value = nextCursorPosition;
};

const triggerMention = async () => {
  if (!textareaRef.value) return;

  const selectionStart = textareaRef.value.selectionStart ?? messageBody.value.length;
  const selectionEnd = textareaRef.value.selectionEnd ?? selectionStart;
  const before = messageBody.value.slice(0, selectionStart);
  const after = messageBody.value.slice(selectionEnd);
  const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
  const mentionStarter = `${needsLeadingSpace ? " " : ""}@`;

  messageBody.value = `${before}${mentionStarter}${after}`;

  await nextTick();

  const nextCursorPosition = before.length + mentionStarter.length;
  textareaRef.value.focus();
  textareaRef.value.setSelectionRange(nextCursorPosition, nextCursorPosition);
  cursorPosition.value = nextCursorPosition;
  updateMentionState(messageBody.value, nextCursorPosition);
  autoResizeTextarea();
};

const handleFileSelection = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selectedFiles = target.files ? Array.from(target.files) : [];
  if (selectedFiles.length === 0) return;

  attachments.value = [...attachments.value, ...selectedFiles];
  syncInputFiles();
};

const removeAttachment = (index: number) => {
  attachments.value = attachments.value.filter((_, current) => current !== index);
  syncInputFiles();
};

type MessageAttachment = DiscussionMessage["attachments"][number];

const messageDisplayName = (message: DiscussionMessage) => message.user.name || message.user.email;

const attachmentExtension = (filename: string) => filename.split(".").pop()?.toUpperCase() || "ARCHIVO";

const isImageAttachment = (attachment: MessageAttachment) => {
  const contentType = attachment.content_type?.toLowerCase() || "";
  if (contentType.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(attachment.filename);
};

const attachmentTypeLabel = (attachment: MessageAttachment) => {
  const contentType = attachment.content_type?.toLowerCase() || "";
  if (contentType === "application/pdf") return "PDF";
  if (contentType.startsWith("image/")) return attachmentExtension(attachment.filename);

  const subtype = contentType.split("/")[1];
  if (subtype) return subtype.toUpperCase();

  return attachmentExtension(attachment.filename);
};

const attachmentMeta = (attachment: MessageAttachment) =>
  `${attachmentTypeLabel(attachment)} · ${formatByteSize(attachment.byte_size)}`;
const mentionedEntities = computed(() => {
  return mentionedEntitiesFromText(messageBody.value, mentionables.value);
});
const mentionedUserIds = computed(() =>
  mentionedEntities.value
    .filter((entity): entity is MentionableUser => entity.type === "user")
    .map((entity) => entity.id),
);
const mentionedGroupIds = computed(() =>
  mentionedEntities.value
    .filter((entity): entity is MentionableGroup => entity.type === "group")
    .map((entity) => entity.id),
);

const handleComposerKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    clearMentionState();
    return;
  }

  if (
    event.key === "Enter" &&
    showMentions.value &&
    filteredMentionables.value.length > 0
  ) {
    event.preventDefault();
    void handleMentionSelect(filteredMentionables.value[0]);
    return;
  }

  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    if (canSendMessage.value) {
      if (event.currentTarget instanceof HTMLTextAreaElement) {
        event.currentTarget.form?.requestSubmit();
      }
    }
  }
};

const queuedAsMessages = computed<DiscussionMessage[]>(() => {
  const user = currentUser.value;
  if (!user) return [];

  const liveClientIds = new Set(
    liveMessages.value.map((message) => message.client_message_id).filter(Boolean),
  );

  return queuedMessages.value
    .filter((item) => !liveClientIds.has(item.id))
    .map((item) => ({
      id: item.sequence ?? (Number.parseInt(item.id.replace(/\D/g, ""), 10) || Date.now()),
      thread_id: props.thread.id,
      body: item.body,
      created_at: item.createdAt,
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
      },
      mention_user_ids: item.mentionUserIds,
      mention_group_ids: item.mentionGroupIds,
      mentions: [
        ...item.mentionUserIds
          .map((id) => mentionables.value.find((entity): entity is MentionableUser => entity.type === "user" && entity.id === id)),
        ...item.mentionGroupIds
          .map((id) => mentionables.value.find((entity): entity is MentionableGroup => entity.type === "group" && entity.id === id)),
      ].filter((entity): entity is MentionableEntity => Boolean(entity)),
      attachments: [],
      client_message_id: item.id,
      status: "queued",
    }));
});

const displayMessages = computed(() =>
  uniqueBy(
    sortByCreatedAt([...liveMessages.value, ...queuedAsMessages.value]),
    (message) => message.client_message_id || `${message.id}-${message.created_at}`,
  ),
);
const emptyStateMessage = computed(() => {
  if (!isOnline.value && hasCachedThread.value === false) {
    return {
      title: "Sin conexión",
      subtitle: "No hay mensajes guardados para este hilo.",
    };
  }

  return {
    title: "Todavía no hay mensajes.",
    subtitle: "Rompe el hielo con la primera respuesta.",
  };
});

const resetComposerState = async () => {
  composerError.value = null;
  messageBody.value = "";
  attachments.value = [];
  clearMentionState();
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }

  await nextTick();
  autoResizeTextarea();
};

const restoreComposerState = async () => {
  const snapshot = composerSnapshot.value;
  if (!snapshot) return;

  messageBody.value = snapshot.body;
  attachments.value = [...snapshot.attachments];
  clearMentionState();
  syncInputFiles();
  composerSnapshot.value = null;

  await nextTick();
  autoResizeTextarea();
};

const handleComposerStart = () => {
  if (!isOnline.value) return;

  composerSnapshot.value = {
    body: messageBody.value,
    attachments: [...attachments.value],
  };
};

const handleComposerError = () => {
  if (!isOnline.value) return;
  void restoreComposerState();
};

const handleComposerCancel = () => {
  if (!isOnline.value) return;
  void restoreComposerState();
};
const handleComposerSuccess = async () => {
  composerSnapshot.value = null;
  await resetComposerState();
};

const handleOfflineSubmit = async () => {
  const orgId = organizationId.value;
  const user = currentUser.value;
  if (!orgId || !user) return;

  if (attachments.value.length > 0) {
    composerError.value = "Los adjuntos requieren conexión.";
    return;
  }

  const body = messageBody.value.trim();
  if (!body) {
    composerError.value = "Escribe un mensaje antes de enviarlo.";
    return;
  }

  const mentionUserIds = [...mentionedUserIds.value];
  const mentionGroupIds = [...mentionedGroupIds.value];
  const queuedItem: OfflineQueuedMessage = {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    organizationId: orgId,
    threadId: props.thread.id,
    body,
    mentionUserIds,
    mentionGroupIds,
    createdAt: new Date().toISOString(),
  };

  await enqueueOfflineMessage(queuedItem);
  await loadQueuedMessages();
  await handleComposerSuccess();
};

const handleFormBefore = () => {
  composerError.value = null;
  if (composerLocked.value) {
    composerError.value = "Este hilo está archivado. Desarchívalo desde el switch de metadata para comentar.";
    return false;
  }

  if (isOnline.value) return true;

  if (attachments.value.length > 0) {
    composerError.value = "Los adjuntos requieren conexión.";
    return false;
  }

  void handleOfflineSubmit();
  return false;
};
</script>

<template>
  <section
    class="flex min-h-0 flex-1 flex-col overflow-hidden"
    :class="containerClass"
  >
    <!-- Thread header -->
    <header
      v-if="props.showHeader"
      class="border-b border-border/60 bg-background/90"
      :class="props.variant === 'desktop' ? 'px-6 py-4 backdrop-blur-md' : 'px-3 py-3 backdrop-blur-sm'"
    >
      <template v-if="props.variant === 'desktop'">
        <div class="flex items-start gap-3">
          <span class="flex size-8 items-center justify-center rounded-lg border border-border/50 bg-muted/60 text-lg">
            {{ threadSnapshot.emoji || "💬" }}
          </span>
          <div class="min-w-0 flex-1">
            <h1 class="truncate text-base font-semibold leading-tight tracking-tight text-foreground">
              {{ threadSnapshot.title }}
            </h1>
            <p
              v-if="threadSnapshot.description"
              class="mt-2 max-w-[80ch] whitespace-pre-wrap text-sm leading-relaxed text-foreground/85"
            >
              <MentionText :text="threadSnapshot.description" :mentionables="mentionables" />
            </p>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex items-center gap-2">
          <Button
            v-if="hasBackLink"
            as-child
            type="button"
            variant="ghost"
            size="icon"
            class="size-8 rounded-full"
          >
            <Link :href="props.backHref || '#'" view-transition class="shrink-0">
              <ArrowLeftIcon class="size-4" />
              <span class="sr-only">Volver a hilos</span>
            </Link>
          </Button>

          <span class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/60 text-lg">
            {{ threadSnapshot.emoji || "💬" }}
          </span>

          <div class="min-w-0 flex-1">
            <h1 class="truncate text-sm font-semibold leading-tight text-foreground">
              {{ threadSnapshot.title }}
            </h1>
            <p class="mt-0.5 truncate text-[11px] text-muted-foreground/90">
              {{ threadSnapshot.creator.name }} · {{ formatDateTime(threadSnapshot.updated_at) }}
            </p>
          </div>
        </div>
      </template>

      <template v-if="props.variant !== 'desktop'">
        <p
          v-if="threadSnapshot.description"
          class="mt-2 whitespace-pre-wrap rounded-xl border border-border/60 bg-background/75 px-3 py-2 text-xs leading-relaxed text-foreground/90"
        >
          <MentionText :text="threadSnapshot.description" :mentionables="mentionables" />
        </p>

        <div class="mt-2">
          <DiscussionThreadTags
            :tags="threadSnapshot.tags"
            :available-tags="[]"
            :organization-id="organizationId || 0"
            :thread-id="threadSnapshot.id"
            :can-edit="false"
            :show-empty="false"
            variant="compact"
          />
        </div>

        <div
          v-if="threadSnapshot.participants && (threadSnapshot.participants.users.length > 0 || threadSnapshot.participants.groups.length > 0)"
          class="mt-2 rounded-xl border border-border/60 bg-background/70 px-3 py-2"
        >
          <p class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Participantes</p>
          <div class="mt-1.5 flex flex-wrap gap-1.5">
            <Badge
              v-for="participant in threadSnapshot.participants.users"
              :key="`user-${participant.id}`"
              variant="secondary"
              class="rounded-full"
            >
              @{{ participant.name }}
            </Badge>
            <GroupHoverCard
              v-for="group in threadSnapshot.participants.groups"
              :key="`group-${group.id}`"
              :name="group.name"
              :members="group.members"
            >
              <Badge variant="outline" class="cursor-default rounded-full">
                @{{ group.name }}
              </Badge>
            </GroupHoverCard>
          </div>
        </div>
      </template>

      <OfflineStateBanner v-if="!isOnline" variant="thread-show" class="mt-2" />
    </header>

    <!-- Messages -->
    <ScrollArea class="min-h-0 flex-1 overflow-hidden">
      <div class="flex-1 space-y-6 px-4 py-4 md:px-5">
        <template v-if="displayMessages.length > 0">
          <template v-for="(message, index) in displayMessages" :key="`${message.id}-${message.created_at}`">
            <!-- Date separator -->
            <div
              v-if="isNewDay(index)"
              class="flex items-center gap-3 py-1"
            >
              <div class="h-px flex-1 bg-border/60" />
              <span class="shrink-0 text-[11px] font-medium text-muted-foreground/60">
                {{ formatDaySeparator(message.created_at) }}
              </span>
              <div class="h-px flex-1 bg-border/60" />
            </div>

            <!-- Full message (first in group) -->
            <div v-if="isFirstInGroup(index)" class="flex gap-3" :class="{ 'mt-1': !isNewDay(index) && index > 0 }">
              <div
                class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                :class="getUserAvatarColor(message.user.id).bg"
              >
                <span
                  class="text-[10px] font-semibold"
                  :class="getUserAvatarColor(message.user.id).text"
                >
                  {{ initialsFor(messageDisplayName(message)) }}
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <div class="mb-1 flex items-baseline gap-2">
                  <span class="text-[13px] font-semibold text-foreground">
                    {{ messageDisplayName(message) }}
                  </span>
                  <span class="text-[11px] text-muted-foreground/50">
                    {{ formatMessageTime(message.created_at) }}
                  </span>
                  <span
                    v-if="message.status === 'queued'"
                    class="rounded-full border border-amber-300/50 bg-amber-100/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  >
                    Se enviara al reconectar
                  </span>
                </div>

                <p class="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/80">
                  <MentionText :text="message.body" :mentionables="mentionables" />
                </p>

                <div v-if="message.attachments?.length" class="mt-2 space-y-2">
                  <a
                    v-for="attachment in message.attachments"
                    :key="`${attachment.filename}-${attachment.url}`"
                    :href="attachment.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block"
                  >
                    <div
                      v-if="isImageAttachment(attachment)"
                      class="max-w-[200px] overflow-hidden rounded-lg border border-border"
                    >
                      <img
                        :src="attachment.url"
                        :alt="attachment.filename"
                        class="max-h-40 w-full object-contain bg-foreground/[0.03]"
                        loading="lazy"
                      />
                      <div class="flex items-center gap-2 border-t border-border/60 px-3 py-1.5">
                        <ImageIcon class="h-3 w-3 text-muted-foreground/50" />
                        <span class="truncate text-[10px] text-muted-foreground/60">{{ attachment.filename }}</span>
                        <span class="ml-auto text-[10px] text-muted-foreground/40">{{ formatByteSize(attachment.byte_size) }}</span>
                      </div>
                    </div>

                    <div
                      v-else
                      class="flex max-w-xs items-center gap-3 rounded-lg border border-border bg-foreground/[0.02] px-3 py-2.5"
                    >
                      <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
                        <PaperclipIcon class="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-[12px] font-medium text-foreground">
                          {{ attachment.filename }}
                        </p>
                        <p class="text-[10px] text-muted-foreground/50">
                          {{ attachmentMeta(attachment) }}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            <!-- Continuation message (same user, within 5min) -->
            <div v-else class="group -mt-3 flex gap-3">
              <div class="flex w-8 flex-shrink-0 items-center justify-end">
                <span class="text-[10px] tabular-nums text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/40">
                  {{ formatMessageTime(message.created_at) }}
                </span>
              </div>
              <div class="min-w-0 flex-1">
                <p class="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/80">
                  <MentionText :text="message.body" :mentionables="mentionables" />
                </p>

                <div v-if="message.attachments?.length" class="mt-2 space-y-2">
                  <a
                    v-for="attachment in message.attachments"
                    :key="`${attachment.filename}-${attachment.url}`"
                    :href="attachment.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block"
                  >
                    <div
                      v-if="isImageAttachment(attachment)"
                      class="max-w-[200px] overflow-hidden rounded-lg border border-border"
                    >
                      <img
                        :src="attachment.url"
                        :alt="attachment.filename"
                        class="max-h-40 w-full object-contain bg-foreground/[0.03]"
                        loading="lazy"
                      />
                      <div class="flex items-center gap-2 border-t border-border/60 px-3 py-1.5">
                        <ImageIcon class="h-3 w-3 text-muted-foreground/50" />
                        <span class="truncate text-[10px] text-muted-foreground/60">{{ attachment.filename }}</span>
                        <span class="ml-auto text-[10px] text-muted-foreground/40">{{ formatByteSize(attachment.byte_size) }}</span>
                      </div>
                    </div>

                    <div
                      v-else
                      class="flex max-w-xs items-center gap-3 rounded-lg border border-border bg-foreground/[0.02] px-3 py-2.5"
                    >
                      <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
                        <PaperclipIcon class="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-[12px] font-medium text-foreground">
                          {{ attachment.filename }}
                        </p>
                        <p class="text-[10px] text-muted-foreground/50">
                          {{ attachmentMeta(attachment) }}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

          </template>
        </template>
        <div
          v-else
          class="flex flex-1 items-center justify-center"
          :class="props.variant === 'desktop' ? 'py-16' : 'py-12'"
        >
          <div
            class="rounded-2xl border border-dashed border-border/60 bg-background/80 text-center"
            :class="props.variant === 'desktop' ? 'px-10 py-10' : 'px-6 py-8'"
          >
            <p class="text-sm font-medium text-muted-foreground">
              {{ emptyStateMessage.title }}
            </p>
            <p class="mt-1 text-xs text-muted-foreground/70">
              {{ emptyStateMessage.subtitle }}
            </p>
          </div>
        </div>
        <div ref="messagesEndRef" />
      </div>
    </ScrollArea>

    <!-- Composer -->
    <section
      v-if="organizationId"
      class="border-t border-border bg-muted/40"
      :class="
        props.variant === 'desktop'
          ? 'px-6 py-4 backdrop-blur-md'
          : 'px-3 py-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]'
      "
    >
      <p
        v-if="threadSnapshot.archived"
        class="mb-2 text-xs text-muted-foreground"
      >
        Este hilo está archivado. Solo puede desarchivarse manualmente desde el switch de metadata.
      </p>

      <Form
        :action="
          organizationDiscussionThreadDiscussionMessagesPath(
            organizationId,
            props.thread.id,
          )
        "
        method="post"
        :on-before="handleFormBefore"
        :show-progress="false"
        :options="{ preserveState: true, preserveScroll: true, replace: true, only: [...MESSAGE_PARTIAL_PROPS], forceFormData: true }"
        disable-while-processing
        class="inert:pointer-events-none inert:opacity-80"
        @start="handleComposerStart"
        @success="handleComposerSuccess"
        @error="handleComposerError"
        @cancel="handleComposerCancel"
        #default="{ errors, processing, progress }"
      >
        <div class="relative">
          <!-- Mention dropdown -->
          <div
            v-if="showMentions && filteredMentionables.length > 0"
            class="absolute bottom-full left-0 right-0 z-10 mb-2 overflow-hidden rounded-2xl border border-border/70 bg-popover ring-1 ring-border/40"
          >
            <ul class="max-h-52 overflow-y-auto">
              <li v-for="entity in filteredMentionables" :key="`${entity.type}-${entity.id}`">
                <button
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-muted/60"
                  @mousedown.prevent
                  @click="handleMentionSelect(entity)"
                >
                  <Avatar class="size-7 border border-border/70">
                    <AvatarFallback class="text-[10px] font-semibold">
                      {{ initialsFor(entity.name) }}
                    </AvatarFallback>
                  </Avatar>
                  <div class="min-w-0">
                    <div class="flex items-center gap-1.5">
                      <p class="truncate text-sm font-medium text-foreground">{{ entity.name }}</p>
                      <span v-if="entity.type === 'group'" class="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Grupo
                      </span>
                    </div>
                    <p class="truncate text-xs text-muted-foreground">
                      {{ entity.type === "user" ? entity.email : (entity.members?.map(m => m.name).join(', ') || '') }}
                    </p>
                  </div>
                </button>
              </li>
            </ul>
          </div>

          <!-- Attachments -->
          <div
            v-if="mentionedEntities.length > 0"
            class="mb-2 -mx-0.5 flex flex-wrap items-center gap-1.5 px-0.5"
          >
            <span class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Menciones
            </span>
            <Badge
              v-for="entity in mentionedEntities"
              :key="`composer-mention-${entity.type}-${entity.id}`"
              :variant="entity.type === 'group' ? 'outline' : 'secondary'"
              class="cursor-default rounded-full"
            >
              @{{ entity.name }}
            </Badge>
          </div>

          <!-- Attachments -->
          <div
            v-if="attachments.length > 0"
            class="mb-2 -mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1"
          >
            <div
              v-for="(file, index) in attachments"
              :key="`${file.name}-${index}`"
              class="flex max-w-[220px] shrink-0 items-center gap-2 rounded-full border border-border/70 bg-background/90 px-3 py-1.5"
            >
              <component
                :is="file.type.startsWith('image/') ? ImageIcon : FileIcon"
                class="size-3.5 text-muted-foreground"
              />
              <span class="truncate text-xs font-medium">{{ file.name }}</span>
              <button
                type="button"
                class="rounded-full p-0.5 text-muted-foreground transition hover:bg-background hover:text-foreground"
                @click="removeAttachment(index)"
              >
                <XIcon class="size-3.5" />
              </button>
            </div>
          </div>

          <div v-if="progress" class="mb-2 space-y-1.5 px-1">
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <span>Subiendo archivos</span>
              <span>{{ Math.round(progress.percentage) }}%</span>
            </div>
            <Progress :model-value="progress.percentage" />
          </div>

          <input
            v-for="userId in mentionedUserIds"
            :key="`mentioned-user-${userId}`"
            type="hidden"
            name="discussion_message[mentioned_user_ids][]"
            :value="userId"
          />
          <input
            v-for="groupId in mentionedGroupIds"
            :key="`mentioned-group-${groupId}`"
            type="hidden"
            name="discussion_message[mentioned_group_ids][]"
            :value="groupId"
          />

          <div
            class="rounded-[1.35rem] border border-border/70 bg-background/95 p-2 transition focus-within:border-primary/35 focus-within:ring-2 focus-within:ring-primary/10"
          >
            <textarea
              ref="textareaRef"
              :value="messageBody"
              name="discussion_message[body]"
              rows="1"
              :disabled="threadSnapshot.archived || processing"
              :placeholder="
                threadSnapshot.archived
                  ? 'Hilo archivado. Desarchívalo desde metadata para comentar.'
                  : 'Escribe un mensaje...'
              "
              required
              class="max-h-32 w-full resize-none border-none bg-transparent px-2 py-1.5 text-sm leading-relaxed text-foreground focus:outline-none"
              :class="props.variant === 'desktop' ? 'min-h-10' : 'min-h-11'"
              @input="handleBodyInput"
              @click="handleCursorUpdate"
              @keyup="handleCursorUpdate"
              @keydown="handleComposerKeydown"
            />

            <p v-if="errors['discussion_message.body'] || errors.body" class="px-2 text-xs text-destructive">
              {{ errors["discussion_message.body"] || errors.body }}
            </p>
            <p v-if="composerError" class="px-2 text-xs text-destructive">
              {{ composerError }}
            </p>

            <div class="mt-1 flex items-center justify-between gap-2">
              <div class="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  class="size-8 rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary"
                  :disabled="threadSnapshot.archived"
                  @click="triggerMention"
                >
                  <AtSignIcon class="size-4" />
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  class="size-8 rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary"
                  :disabled="threadSnapshot.archived || !isOnline"
                  @click="openFilePicker"
                >
                  <PaperclipIcon class="size-4" />
                </Button>

                <input
                  ref="fileInputRef"
                  type="file"
                  name="discussion_message[attachments][]"
                  multiple
                  :disabled="threadSnapshot.archived || !isOnline"
                  class="hidden"
                  @change="handleFileSelection"
                />
              </div>

              <Button
                type="submit"
                size="icon"
                class="size-9 rounded-full border border-primary/25"
                :disabled="processing || !canSendMessage"
              >
                <SendIcon class="size-4" />
              </Button>
            </div>
          </div>
        </div>

      </Form>
    </section>
  </section>
</template>
